import React, { FC, useEffect } from "react";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useContact } from "@api";

import { usePartnerInfo, withController } from "@utils";

import { Modal } from "@ui/components";
import { Button, Divider, Icon, Notification, Text, Title } from "@ui/elements";
import { Input, Textarea } from "@ui/forms";
import { Grid, Stack } from "@ui/layout";

import { toaster, useAuth } from "@components";

import { FAQ } from "../FAQ/FAQ";

type ContactModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type ContactFormFields = {
  name: string;
  email: string;
  message: string;
  accept_privacy_policy: string;
};

const ControlledInput = withController<ContactFormFields>()(Input);
const ControlledTextarea = withController<ContactFormFields>()(Textarea);

export const ContactModal: FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const authContext = useAuth();
  const partnerInfo = usePartnerInfo();
  const {
    mutateAsync: sendContact,
    isPending: contactLoading,
    error: contactError,
  } = useContact();
  const { control, formState, setValue, handleSubmit } =
    useForm<ContactFormFields>({
      defaultValues: {
        name: authContext.userAttributes?.name,
        email: authContext.userAttributes?.email,
      },
    });

  useEffect(() => {
    if (authContext.userAttributes) {
      if (!formState.touchedFields.name && authContext.userAttributes.name) {
        setValue("name", authContext.userAttributes.name);
      }
      if (!formState.touchedFields.email && authContext.userAttributes.email) {
        setValue("email", authContext.userAttributes.email);
      }
    }
  }, [formState.touchedFields, authContext.userAttributes]);

  const onSubmit = async (values: ContactFormFields) => {
    const captchaToken = await executeRecaptcha?.("contact_form");
    await sendContact({
      data: {
        name: values.name,
        email: values.email,
        message: values.message,
        recaptchaToken: captchaToken as string,
      },
    });

    toaster.success({
      title: t("messages.message-sent"),
      message: t("messages.message-sent-info"),
    });

    setValue("message", "");
  };

  return (
    <Modal
      header={
        <Stack>
          <Icon icon="Info" />
          <Title size={4}>{t("menu.get-help")}</Title>
        </Stack>
      }
      isActive={isOpen}
      onClose={onClose}
    >
      <Stack direction="column" gap="xxl">
        <Stack direction="column">
          <Title size={5}>{t("frequently-asked-questions")}</Title>
          <FAQ />
        </Stack>
        <Stack direction="column">
          <Title size={5}>{t("contact-us")}</Title>
          <Stack direction="column" gap="xl">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack direction="column" gap="xl">
                <Stack direction="column">
                  <Grid>
                    <ControlledInput
                      label={t("labels.name")}
                      name="name"
                      control={control}
                    />
                    <ControlledInput
                      label={t("labels.email")}
                      type="email"
                      name="email"
                      control={control}
                    />
                  </Grid>
                  <ControlledTextarea
                    rows={4}
                    control={control}
                    name="message"
                    label={t("labels.message")}
                  />
                </Stack>
                {contactError && (
                  <Notification color="danger">
                    <Text weight="bold">
                      {contactError?.response?.data.error}
                    </Text>
                    <Text>{contactError?.response?.data.message}</Text>
                  </Notification>
                )}
                <Stack direction="column" gap="xs">
                  <Button variant="light" isLoading={contactLoading}>
                    {t("labels.send")}
                  </Button>
                  <Text
                    variant="secondary"
                    size="small"
                    dangerouslySetInnerHTML={{ __html: t("recaptchaInfo") }}
                  />
                </Stack>
              </Stack>
            </form>
            <Divider text={t("labels.or")} />
            <Stack justify="center">
              {partnerInfo.contact.telegram && (
                <Button
                  variant="light"
                  isRounded
                  as="link"
                  href={partnerInfo.contact.telegram}
                  target="_blank"
                >
                  <Icon icon="TelegramLogo" />
                </Button>
              )}
              {partnerInfo.contact.whatsapp && (
                <Button
                  variant="light"
                  isRounded
                  as="link"
                  href={partnerInfo.contact.whatsapp}
                  target="_blank"
                >
                  <Icon icon="WhatsappLogo" />
                </Button>
              )}
              {partnerInfo.contact.facebook && (
                <Button
                  variant="light"
                  isRounded
                  as="link"
                  href={partnerInfo.contact.facebook}
                  target="_blank"
                >
                  <Icon icon="FacebookLogo" />
                </Button>
              )}
              {partnerInfo.contact.tiktok && (
                <Button
                  variant="light"
                  isRounded
                  as="link"
                  href={partnerInfo.contact.tiktok}
                  target="_blank"
                >
                  <Icon icon="TiktokLogo" />
                </Button>
              )}
              {partnerInfo.contact.instagram && (
                <Button
                  variant="light"
                  isRounded
                  as="link"
                  href={partnerInfo.contact.instagram}
                  target="_blank"
                >
                  <Icon icon="InstagramLogo" />
                </Button>
              )}
              {partnerInfo.contact.email && (
                <Button
                  variant="light"
                  isRounded
                  as="link"
                  href={`mailto:${partnerInfo.contact.email}`}
                  target="_blank"
                >
                  <Icon icon="At" />
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};
